import React from 'react';
import { PersonDataContext } from '../../contexts/person-data-context';
import {
  UploadDocument,
  getDocumentFileNames,
  isVisibleUpload,
  nextStatusChangeAt,
  toUploadDocument,
} from '../../utils/upload-activities';
import { useUploadActivities } from '../use-data';
import { useAvailableDocIds } from '../use-upload-polling';

/**
 * Re-renders the caller at the given time
 */
const useRenderAt = (timestamp?: number) => {
  const [, rerender] = React.useReducer((count: number) => count + 1, 0);

  React.useEffect(() => {
    if (timestamp === undefined) {
      return undefined;
    }
    const timeout = setTimeout(rerender, Math.max(timestamp - Date.now(), 0));
    return () => clearTimeout(timeout);
  }, [timestamp]);
};

/**
 * @returns Recent uploads that aren't available as documents yet, as document
 * list entries labelled with their processing status
 */
export const useUploadDocuments = (): UploadDocument[] => {
  const { personData } = React.useContext(PersonDataContext);
  const { data } = useUploadActivities();
  const availableDocIds = useAvailableDocIds();

  const documentFileNames = React.useMemo(
    () => getDocumentFileNames(personData),
    [personData],
  );

  const now = Date.now();
  const visible = (data?.activities ?? []).filter((activity) =>
    isVisibleUpload(activity, now, { availableDocIds, documentFileNames }),
  );

  useRenderAt(nextStatusChangeAt(visible, now));

  return visible.map((activity) => toUploadDocument(activity, now));
};
